import useMediaQuery from '@mui/material/useMediaQuery';
import { useCallback, useMemo, useState } from 'react';
import BaseTable from 'components/Tables/BaseTable';
import { defaultPlaceType, placeTypes } from '../../config/placeTypes.config';
import PlaceTitleForm from '../PlaceTitleForm/PlaceTitleForm';
import SavedPlaceActions from '../SavedPlaceActions/SavedPlaceActions';
import SavedPlacesAccordion from '../SavedPlacesAccordion/SavedPlacesAccordion';

const getSearchableText = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/\s/g, '');

/** Lists saved places and owns the title draft for the place being renamed. */
const SavedPlacesTable = ({
  places = [],
  search = '',
  selectedPlaceId,
  canEditPlaces,
  canChangeHomePlace,
  isClassic = false,
  onSelectPlace,
  onRenamePlace,
  onChangeHomePlace,
  onDeletePlace,
}) => {
  const [renameDraft, setRenameDraft] = useState(null);
  const isMobile = useMediaQuery('(max-width: 767.98px)');

  const saveTitle = useCallback(
    async (place, title) => {
      const wasSaved = await onRenamePlace(place, title);
      if (!wasSaved) return;

      // A completed request must not discard a different or newly edited draft.
      setRenameDraft((current) =>
        current?.placeId === place.place_id && current.title === title ? null : current,
      );
    },
    [onRenamePlace],
  );

  const makeHomePlace = useCallback(
    async (place) => {
      const homePlace = await onChangeHomePlace(place);
      if (homePlace) setRenameDraft(null);
    },
    [onChangeHomePlace],
  );

  const renderTitleForm = useCallback(
    (place) => (
      <PlaceTitleForm
        title={renameDraft.title}
        isClassic={isClassic}
        onChange={(title) => setRenameDraft({ placeId: place.place_id, title })}
        onSave={() => saveTitle(place, renameDraft.title)}
      />
    ),
    [isClassic, renameDraft, saveTitle],
  );

  const renderActions = useCallback(
    (place, showLabels = false) => {
      const isEditing = renameDraft?.placeId === place.place_id;
      return (
        <SavedPlaceActions
          place={place}
          canEditPlaces={canEditPlaces}
          canChangeHomePlace={canChangeHomePlace}
          isEditing={isEditing}
          showLabels={showLabels}
          onRenamePlace={() => {
            if (isEditing) saveTitle(place, renameDraft.title);
            else setRenameDraft({ placeId: place.place_id, title: place.title || '' });
          }}
          onChangeHomePlace={makeHomePlace}
          onDeletePlace={onDeletePlace}
        />
      );
    },
    [canChangeHomePlace, canEditPlaces, makeHomePlace, onDeletePlace, renameDraft, saveTitle],
  );

  const columns = useMemo(() => {
    return [
      {
        header: 'Title',
        accessorKey: 'title',
        Cell: ({ row, cell }) => {
          const { place, TypeIcon } = row.original;
          const isEditing = canEditPlaces && renameDraft?.placeId === place.place_id;

          return (
            <div className="d-flex align-items-center gap-2 min-w-0">
              <span className="user-menu-place-type-icon" aria-hidden="true">
                <TypeIcon size={20} color="currentColor" />
              </span>
              {isEditing ? renderTitleForm(place) : <p className="mb-0">{cell.getValue()}</p>}
            </div>
          );
        },
        size: 160,
      },
      {
        header: 'Type',
        accessorKey: 'type',
        Cell: ({ cell }) => <p className="mb-0">{cell.getValue()}</p>,
        size: 90,
      },
      {
        header: 'Location',
        accessorKey: 'location',
        Cell: ({ cell }) => <p className="mb-0">{cell.getValue()}</p>,
        size: 180,
      },
      {
        header: '',
        id: 'Actions',
        columnDefType: 'display',
        Cell: ({ row }) => renderActions(row.original.place),
        enableSorting: false,
        size: 120,
      },
    ];
  }, [canEditPlaces, renameDraft, renderActions, renderTitleForm]);

  const rows = useMemo(() => {
    const searchText = getSearchableText(search);

    return places
      .filter(
        (place) =>
          getSearchableText(place.title).includes(searchText) ||
          getSearchableText(place.state).includes(searchText),
      )
      .map((place) => {
        const type = Object.hasOwn(placeTypes, place.type)
          ? placeTypes[place.type]
          : defaultPlaceType;
        return {
          id: place.place_id,
          title: place.title,
          TypeIcon: type.Icon,
          type: type.label,
          location: place.place_data?.formatted_address,
          place,
        };
      });
  }, [places, search]);

  return isMobile ? (
    <SavedPlacesAccordion
      rows={rows}
      selectedPlaceId={selectedPlaceId}
      editingPlaceId={canEditPlaces ? renameDraft?.placeId : null}
      emptyMessage={search ? 'No places match your search.' : 'No saved places.'}
      onSelectPlace={onSelectPlace}
      renderTitleForm={renderTitleForm}
      renderActions={renderActions}
    />
  ) : (
    <BaseTable
      columns={columns}
      rows={rows}
      selectedRowId={selectedPlaceId}
      enableRowVirtualization={false}
      layoutMode="semantic"
      customStyles={{
        header: { minWidth: 0 },
        cell: { minWidth: 0, padding: '8px', whiteSpace: 'normal', overflowWrap: 'anywhere' },
      }}
      onRowClick={(row) => onSelectPlace(row.place)}
    />
  );
};

export default SavedPlacesTable;
